import React, { useState } from 'react';
import { VersionTag } from 'features/version';
import {
	InstanceMark,
	instanceEnvironmentShort,
	useInstance,
} from 'features/instance';
import {
	faHome,
	faUsers,
	faCogs,
	faIndustry,
	faBuilding,
	faBox,
	faReceipt,
	faFileInvoiceDollar,
	faFileContract,
	faRepeat,
	faChartLine,
	faClipboardList,
	faMoneyBillTransfer,
	faBriefcase,
	faMoneyBills,
	faAddressCard,
	faChartBar,
	faArrowRightArrowLeft,
	faPersonArrowDownToLine,
} from '@fortawesome/free-solid-svg-icons';
import SidebarLink from './SidebarLinks';
import SidebarMenu from './SidebarMenu';

const Sidebar: React.FC = () => {
	const [openMenu, setOpenMenu] = useState<
		'customers' | 'suppliers' | 'badges' | null
	>(null);

	const handleMenuClick = (menu: 'customers' | 'suppliers' | 'badges') => {
		setOpenMenu((prev) => (prev === menu ? null : menu)); // toggle
	};

	const closeMenus = () => setOpenMenu(null); // pour les liens simples
	const { branding } = useInstance();
	const displayName = branding?.displayName?.trim() ?? '';
	const nonProd = branding != null && branding.profile !== 'production';
	const environmentShort = branding
		? instanceEnvironmentShort(branding.profile)
		: null;

	return (
		<nav className="w-64 bg-gray-800 text-white flex flex-col p-4 space-y-2 relative h-screen">
			<div className="flex items-center gap-3 mb-4 min-w-0">
				<InstanceMark className="w-9 h-9 object-contain shrink-0" />
				{displayName && (
					<span
						className={`min-w-0 font-semibold leading-tight truncate ${
							nonProd ? 'text-amber-400' : 'text-white'
						}`}
						title={displayName}
					>
						{displayName}
					</span>
				)}
				{environmentShort && (
					<span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-amber-200 border border-amber-400/60 rounded px-1.5 py-0.5">
						{environmentShort}
					</span>
				)}
			</div>

			<SidebarLink
				to="/app"
				label="Accueil"
				icon={faHome}
				matchExact
				onClick={closeMenus}
			/>
			<SidebarLink
				to="/app/users"
				label="Utilisateurs"
				icon={faUsers}
				onClick={closeMenus}
				requiredModule="users"
			/>
			<SidebarMenu
				label="Badges"
				icon={faAddressCard}
				basePath="/app/badgeMovements"
				isOpen={openMenu === 'badges'}
				onToggle={() => handleMenuClick('badges')}
			>
				<SidebarLink
					to="/app/badgeMovements"
					label="Mouvements"
					icon={faArrowRightArrowLeft}
					requiredModule="badge"
				/>
				<SidebarLink
					to="/app/activities/from-badge"
					label="Générer activités"
					icon={faPersonArrowDownToLine}
					requiredPermissions={[
						// Lecture sur tous les modules nécessaires à la génération
						{ module: 'badge', action: 'read' },
						{ module: 'activites', action: 'read' },
						{ module: 'devis_client', action: 'read' }, // éléments logistiques
						{ module: 'services', action: 'read' },
						{ module: 'produits', action: 'read' },
						{ module: 'affaires', action: 'read' },
						// Écriture sur badge et activités (création réelle)
						{ module: 'badge', action: 'write' },
						{ module: 'activites', action: 'write' },
					]}
				/>
			</SidebarMenu>

			<SidebarMenu
				label="Clients"
				icon={faBuilding}
				basePath="/app/customers"
				isOpen={openMenu === 'customers'}
				onToggle={() => handleMenuClick('customers')}
			>
				<div className="border-l border-white mx-2 my-2">
					<SidebarLink
						to="/app/quotes"
						label="Devis"
						icon={faFileContract}
						className="py-1"
						requiredModule="devis_client"
					/>
					<SidebarLink
						to="/app/customers"
						label="Clients"
						icon={faBuilding}
						className="py-1"
						requiredModule="clients"
					/>
					<SidebarLink
						to="/app/customerOrders"
						label="Commandes"
						icon={faBox}
						className="py-1"
						requiredModule="commandes_client"
					/>
				</div>

				<div className="border-l border-white mx-2 my-2">
					<SidebarLink
						to="/app/projects"
						label="Affaires"
						icon={faBriefcase}
						className="py-1"
						requiredModule="affaires"
					/>
					<SidebarLink
						to="/app/activities"
						label="Activités"
						icon={faChartLine}
						className="py-1"
						requiredModule="activites"
					/>
					<SidebarLink
						to="/app/logisticElements"
						label="Logistique"
						icon={faClipboardList}
						className="py-1"
						requiredModule="devis_client"
					/>
				</div>

				<div className="border-l border-white mx-2 my-2">
					<SidebarLink
						to="/app/customerInvoices"
						label="Factures"
						icon={faFileInvoiceDollar}
						className="py-1"
						requiredModule="factures_client"
					/>
					<SidebarLink
						to="/app/advances"
						label="Acomptes"
						icon={faMoneyBills}
						className="py-1"
						requiredModule="factures_client"
					/>
					<SidebarLink
						to="/app/creditNotes"
						label="Avoirs"
						icon={faMoneyBillTransfer}
						className="py-1"
						requiredModule="factures_client"
					/>
				</div>
			</SidebarMenu>

			<SidebarMenu
				label="Fournisseurs"
				icon={faIndustry}
				basePath="/app/suppliers"
				isOpen={openMenu === 'suppliers'}
				onToggle={() => handleMenuClick('suppliers')}
			>
				<SidebarLink
					to="/app/suppliers"
					label="Fournisseurs"
					icon={faIndustry}
					matchExact
					requiredModule="fournisseurs"
				/>
				<SidebarLink
					to="/app/orders"
					label="Commandes"
					icon={faBox}
					requiredModule="commandes_fournisseurs"
				/>
				<SidebarLink
					to="/app/contracts"
					label="Contrats"
					icon={faRepeat}
					requiredModule="contrats"
				/>
				<SidebarLink
					to="/app/expenses"
					label="Dépenses"
					icon={faReceipt}
					requiredModule="depenses"
				/>
				<SidebarLink
					to="/app/supplierInvoices"
					label="Factures"
					icon={faFileInvoiceDollar}
					requiredModule="factures_fournisseurs"
				/>
			</SidebarMenu>

			<SidebarLink
				to="/app/results"
				label="Résultats"
				icon={faChartBar}
				onClick={closeMenus}
				requiredPermissions={[
					{ module: 'resultats' },
					{ module: 'activites' },
					{ module: 'factures_client' },
					{ module: 'affaires' },
				]}
			/>

			<SidebarLink
				to="/app/settings"
				label="Paramètres"
				icon={faCogs}
				onClick={closeMenus}
				requiredModule="users"
			/>

			<div className="absolute bottom-0 left-0 right-0 border-t border-gray-600 bg-gray-800 py-3 flex justify-center mx-3">
				<VersionTag />
			</div>
		</nav>
	);
};

export default Sidebar;
